/**
 * L'ANALYSE DE CAUSE EN CASES — module PUR (RQ, 03/10/2026 ; étiologies cochées 08/10/2026).
 *
 * Fait constaté · cause retenue · diagramme d'Ishikawa COCHÉ (5 M + mesure).
 * Ce qu'on affiche ici est ce que le serveur range dans `ged-nc.analyse` ; le
 * texte `causes` en est dérivé CÔTÉ SERVEUR (`backend/src/analyse-capa.ts`).
 *
 * Les « 5 pourquoi » ne se saisissent plus, et l'Ishikawa ne se tape plus : on
 * coche des étiologies dans une liste fermée. Une analyse rédigée avant (des
 * « pourquoi », un Ishikawa en texte) s'ouvre avec son contenu recopié en
 * TEXTE LIBRE, mot pour mot — rien n'est deviné ni effacé.
 *
 * ⚠️ Les libellés des familles et des étiologies sont recopiés du serveur :
 * `qualite-analyse-capa.test.js` les compare.
 */

export const CLES_M = ['main_oeuvre', 'materiel', 'methode', 'milieu', 'matiere', 'mesure'] as const;
export type CleM = typeof CLES_M[number];

export interface AnalyseCapa {
  fait: string;
  causeRetenue: string;
  /** Les codes d'étiologies cochés, par famille. */
  ishikawa: Record<CleM, string[]>;
  libre: string;
}

export const FAIT_CONSTATE = {
  libelle: 'Fait constaté',
  explication: 'Ce qui a été observé, sans interprétation : quoi, où, quand, combien. '
    + 'Pas la cause, pas le coupable — elles viennent après.',
};

export const CAUSE_RETENUE = {
  libelle: 'Cause retenue',
  explication: 'La cause racine sur laquelle on agit — celle que l’action corrective va supprimer. '
    + 'Une seule phrase, qui doit se retrouver dans l’action corrective.',
};

/** Une étiologie cochable : `code` est ce qui est enregistré, `libelle` ce qui est lu. */
export interface Etiologie { code: string; libelle: string }

const e = (code: string, libelle: string): Etiologie => ({ code, libelle });

export const ISHIKAWA = {
  titre: 'Diagramme d’Ishikawa (5 M + mesure)',
  explication: 'Cochez les causes POSSIBLES, famille par famille, pour ne pas oublier de piste. '
    + 'Ne cochez que ce qui a joué ; ce qui n’entre dans aucune case se dit en texte libre, plus bas.',
  familles: [
    {
      cle: 'main_oeuvre',
      libelle: 'Main-d’œuvre',
      explication: 'Les personnes : formation, habilitation, charge de travail, consigne comprise ou non.',
      etiologies: [
        e('MO-FORMATION', 'Formation non faite, incomplète ou non enregistrée'),
        e('MO-HABILITATION', 'Poste tenu sans habilitation'),
        e('MO-CONSIGNE-IGNOREE', 'Consigne non connue ou mal comprise'),
        e('MO-CONSIGNE-NON-SUIVIE', 'Consigne connue mais non appliquée'),
        e('MO-ARRIVANT', 'Nouvel arrivant ou remplaçant sans accompagnement'),
        e('MO-CHARGE', 'Charge de travail ou interruptions trop fortes'),
        e('MO-EFFECTIF', 'Effectif insuffisant au moment du fait'),
        e('MO-FATIGUE', 'Fatigue, garde de nuit ou fin de poste'),
        e('MO-TRANSMISSION', 'Information non transmise entre deux personnes ou deux postes'),
        e('MO-GESTE', 'Geste habituel mal exécuté (inattention ponctuelle)'),
      ],
    },
    {
      cle: 'materiel',
      libelle: 'Matériel',
      explication: 'Les équipements : panne, étalonnage, maintenance, logiciel.',
      etiologies: [
        e('MATL-PANNE', 'Panne ou dysfonctionnement de l’appareil'),
        e('MATL-MAINTENANCE', 'Maintenance préventive non faite ou en retard'),
        e('MATL-CALIBRATION', 'Calibration ou étalonnage absent, ancien ou non conforme'),
        e('MATL-METROLOGIE', 'Vérification métrologique absente ou non conforme'),
        e('MATL-INADAPTE', 'Appareil ou accessoire inadapté à l’usage'),
        e('MATL-LOGICIEL', 'Logiciel embarqué : version ou paramétrage de l’appareil'),
        e('MATL-SIL', 'Système informatique, interface automate–SIL ou paramétrage'),
        e('MATL-INDISPONIBLE', 'Appareil indisponible, aucune solution de repli'),
        e('MATL-ENTRETIEN', 'Entretien ou nettoyage de l’appareil non fait'),
      ],
    },
    {
      cle: 'methode',
      libelle: 'Méthode',
      explication: 'Les procédures et modes opératoires : absents, flous, périmés, non appliqués.',
      etiologies: [
        e('METH-ABSENTE', 'Aucune procédure ni mode opératoire pour ce cas'),
        e('METH-FLOUE', 'Procédure imprécise, interprétable'),
        e('METH-PERIMEE', 'Document périmé ou non mis à jour'),
        e('METH-NON-APPLIQUEE', 'Procédure existante non appliquée'),
        e('METH-NON-DIFFUSEE', 'Procédure non diffusée, non lue ou introuvable au poste'),
        e('METH-CONTRADICTION', 'Deux documents ou deux consignes qui se contredisent'),
        e('METH-NON-VERIFIEE', 'Méthode mise en service sans vérification de ses performances'),
        e('METH-ENREGISTREMENT', 'Enregistrement prévu non tenu (traçabilité)'),
        e('METH-ORGANISATION', 'Organisation du travail, circuit ou répartition des tâches'),
      ],
    },
    {
      cle: 'milieu',
      libelle: 'Milieu',
      explication: 'L’environnement : température, locaux, bruit, éclairage, horaires.',
      etiologies: [
        e('MIL-TEMPERATURE', 'Température d’un local ou d’une enceinte'),
        e('MIL-LOCAL', 'Local inadapté, exigu ou mal agencé'),
        e('MIL-HYGIENE', 'Hygiène, nettoyage ou désinfection des locaux'),
        e('MIL-INTERRUPTIONS', 'Bruit, passage ou interruptions au poste'),
        e('MIL-ECLAIRAGE', 'Éclairage insuffisant'),
        e('MIL-ENERGIE', 'Coupure d’électricité, d’eau ou de réseau'),
        e('MIL-AFFLUENCE', 'Affluence, pic d’activité ou horaire de pointe'),
        e('MIL-CONFIDENTIALITE', 'Confidentialité ou conditions d’accueil du patient'),
        e('MIL-ACHEMINEMENT', 'Conditions de transport ou d’acheminement'),
      ],
    },
    {
      cle: 'matiere',
      libelle: 'Matière',
      explication: 'Ce qu’on manipule : échantillon, réactif, consommable, lot, transport.',
      etiologies: [
        e('MATR-ECHANTILLON', 'Échantillon non conforme (volume, tube, hémolyse, coagulé)'),
        e('MATR-IDENTIFICATION', 'Identification ou étiquetage de l’échantillon'),
        e('MATR-PRELEVEMENT', 'Geste de prélèvement ou ordre des tubes'),
        e('MATR-PREPARATION', 'Préparation du patient non respectée (jeûne, horaire, traitement)'),
        e('MATR-CONSERVATION', 'Conservation ou délai avant analyse'),
        e('MATR-PRESCRIPTION', 'Prescription absente, incomplète ou illisible'),
        e('MATR-REACTIF-LOT', 'Lot de réactif défectueux'),
        e('MATR-REACTIF-PERIME', 'Réactif ou consommable périmé'),
        e('MATR-STOCKAGE', 'Réactif ou consommable mal stocké'),
        e('MATR-RUPTURE', 'Rupture de stock'),
        e('MATR-CONSOMMABLE', 'Consommable défectueux (tube, embout, cuvette)'),
      ],
    },
    {
      cle: 'mesure',
      libelle: 'Mesure',
      explication: 'Le contrôle : CQI, calibration, critère d’acceptation, indicateur qui n’a pas alerté.',
      etiologies: [
        e('MES-CQI-ABSENT', 'Contrôle interne non passé avant la série'),
        e('MES-CQI-NON-TRAITE', 'Contrôle interne hors limites non vu ou non traité'),
        e('MES-REGLES', 'Limites ou règles de contrôle mal réglées'),
        e('MES-EEQ', 'Évaluation externe non exploitée'),
        e('MES-CRITERE', 'Critère d’acceptation absent ou inadapté'),
        e('MES-INDICATEUR', 'Indicateur ou alerte qui n’a pas alerté'),
        e('MES-RELECTURE', 'Double contrôle ou relecture prévus non faits'),
        e('MES-VALIDATION', 'Validation biologique ou supervision insuffisante'),
        e('MES-ECART-CONNU', 'Écart déjà repéré auparavant et non suivi'),
      ],
    },
  ] as { cle: CleM; libelle: string; explication: string; etiologies: Etiologie[] }[],
};

export const LIBRE = {
  titre: 'Autres éléments d’analyse (texte libre)',
  explication: 'Ce qu’aucune étiologie ne dit — et les analyses rédigées avant les étiologies, '
    + 'conservées telles quelles.',
};

export const CORRECTION_DIRECTE = {
  libelle: 'Correction directe',
  explication: 'Ce qui a été fait tout de suite pour traiter l’EFFET : refaire le tube, rappeler le résultat, isoler le lot.',
};

export const ACTION_CORRECTIVE = {
  libelle: 'Action corrective',
  explication: 'Ce qui supprime la CAUSE retenue, pour que l’écart ne se reproduise pas. La correction directe ne la remplace pas.',
};

export function familleDe(cle: CleM) {
  return ISHIKAWA.familles.find((f) => f.cle === cle)!;
}

/**
 * Le CATALOGUE des étiologies affiché : le socle ci-dessus n'est qu'un REPLI.
 *
 * Le laboratoire reformule, désactive et ajoute des étiologies depuis
 * Paramétrage (collection `ged-nc-vocabulaire`) ; l'écran lit la liste fusionnée
 * par le moteur (`GET ged/nc-vocabulaire`). Tant qu'elle n'est pas arrivée, on
 * affiche le socle plutôt qu'un Ishikawa vide — un panneau sans case se lirait
 * « il n'y a rien à cocher ».
 *
 * Les INACTIVES sont gardées dans le catalogue de LECTURE (pour afficher le
 * libellé d'une NC ancienne) et retirées de ce qui est proposé à la saisie.
 */
export type CatalogueEtiologies = Record<CleM, Etiologie[]>;

export function catalogueSocle(): CatalogueEtiologies {
  return Object.fromEntries(CLES_M.map((k) => [k, familleDe(k).etiologies])) as CatalogueEtiologies;
}

/** Le libellé d'une étiologie, ou son code à défaut — jamais rien. */
export function libelleEtiologie(cle: CleM, code: string, cat?: CatalogueEtiologies): string {
  const liste = cat?.[cle] || familleDe(cle).etiologies;
  return liste.find((x) => x.code === code)?.libelle || code;
}

export function analyseVide(): AnalyseCapa {
  return {
    fait: '', causeRetenue: '',
    ishikawa: Object.fromEntries(CLES_M.map((k) => [k, [] as string[]])) as Record<CleM, string[]>,
    libre: '',
  };
}

/** Ce qu'une analyse enregistrée (version 1 ou 2) devient à l'écran. Même règle que le serveur
 *  (`migrerAnalyse`) : ce qui était rédigé passe au texte libre, sans être réécrit. */
export const TITRE_ANALYSE_V1 = 'Analyse rédigée avant les étiologies cochées :';

export function migrerAnalyse(brut: Record<string, unknown>): AnalyseCapa {
  const a = analyseVide();
  const txt = (v: unknown) => String(v ?? '').replace(/\r\n/g, '\n').trim();
  a.fait = txt(brut.fait);
  a.causeRetenue = txt(brut.causeRetenue);
  a.libre = txt(brut.libre);
  const ish = (brut.ishikawa && typeof brut.ishikawa === 'object' && !Array.isArray(brut.ishikawa)
    ? brut.ishikawa : {}) as Record<string, unknown>;
  const reprises: string[] = [];
  (Array.isArray(brut.pourquoi) ? brut.pourquoi : []).slice(0, 5).forEach((p, i) => {
    const v = txt(p);
    if (v) reprises.push(`${i + 1}. Pourquoi ? ${v}`);
  });
  for (const k of CLES_M) {
    const v = ish[k];
    if (Array.isArray(v)) {
      /* Les codes sont repris TELS QUELS — même règle que le serveur : les
         écarter ici les perdrait au premier réenregistrement, sans un mot. */
      a.ishikawa[k] = v.map((c) => String(c ?? '')).filter(Boolean);
    } else {
      const t = txt(v);
      if (t) reprises.push(`- ${familleDe(k).libelle} : ${t}`);
    }
  }
  if (reprises.length) {
    a.libre = [a.libre, `${TITRE_ANALYSE_V1}\n${reprises.join('\n')}`].filter(Boolean).join('\n\n');
  }
  return a;
}

/** Ce que l'éditeur ouvre : les cases enregistrées, ou — pour une NC analysée avant les cases — son texte
 *  d'origine rangé en « texte libre », sans jamais le réécrire. */
export function depuisNc(nc: { analyse?: unknown; causes?: string | null }): AnalyseCapa {
  const a = nc.analyse;
  if (a && typeof a === 'object' && !Array.isArray(a)) return migrerAnalyse(a as Record<string, unknown>);
  const base = analyseVide();
  base.libre = String(nc.causes ?? '').trim();
  return base;
}

export function estVide(a: AnalyseCapa): boolean {
  return !a.fait.trim() && !a.causeRetenue.trim() && !a.libre.trim()
    && CLES_M.every((k) => !a.ishikawa[k].length);
}

export function sontEgales(a: AnalyseCapa, b: AnalyseCapa): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Coche ou décoche une étiologie — l'ordre suit celui de la liste, pas celui des clics.
 *  Un code déjà coché qui ne serait plus au catalogue est conservé : décocher est un
 *  geste, le rangement n'en est pas un. */
export function basculerEtiologie(a: AnalyseCapa, cle: CleM, code: string, cat?: CatalogueEtiologies): AnalyseCapa {
  const avant = a.ishikawa[cle] || [];
  if (avant.includes(code)) return { ...a, ishikawa: { ...a.ishikawa, [cle]: avant.filter((c) => c !== code) } };
  const liste = (cat?.[cle] || familleDe(cle).etiologies).map((x) => x.code);
  const rangees = liste.filter((c) => c === code || avant.includes(c));
  const hors = avant.filter((c) => !liste.includes(c));
  return { ...a, ishikawa: { ...a.ishikawa, [cle]: [...rangees, ...hors] } };
}
